import { observer } from 'mobx-react-lite'
import { MultiStepMobileWizard } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/MultiStepMobileWizard'
import { SubmitStepProps } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/Types'
import { FlowProps, TextValues } from 'components/Wizards/MobileWizards/Types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { RefineDetails } from 'components/Wizards/MobileWizards/MobileWizard/components/RefineDetails/RefineDetails'
import { useMobileWizardCreateDoc } from 'components/Wizards/MobileWizards/hooks/useMobileWizardCreateDoc'
import { GeneratedContent } from '../../components/GeneratedContent/GeneratedContent'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { AlmanacFile } from 'types'
import { useState } from 'react'

type Values = Pick<TextValues, 'filename' | 'topic' | 'language' | 'tone' | 'brand_kit_id'>

export const FlowZ1 = observer(({ schema, onClose, initialValues, ...props }: FlowProps<ChannelType>) => {
  const {
    meta: { channel, title },
  } = schema

  const { createDoc, isLoading } = useMobileWizardCreateDoc(channel, {
    navigateToDocOnComplete: false,
  })
  const [almanacFile, setAlmanacFile] = useState<AlmanacFile>()
  const [requestId, setRequestId] = useState<number>()

  const createDocumentAndGenerate = async (values: Values) => {
    try {
      const docData: TextValues = {
        ...values,
        filename: values.filename || `${title || 'Generated Caption'} - ${Date.now()}`,
      }

      const result = await createDoc(docData)

      if (!result?.id || !result?.pending_ai_suggestion_request?.id) {
        throw new Error()
      }

      setAlmanacFile(result)
      setRequestId(result.pending_ai_suggestion_request?.id)

      return true
    } catch {
      showToastMessage('An error occured when creating your captions, please try again', 'error')
      return false
    }
  }

  const handleSubmitStep = async ({ values, currentStep, advance }: SubmitStepProps<Values>) => {
    if (currentStep === 0) {
      const success = await createDocumentAndGenerate(values)
      if (success) advance()
    } else {
      onClose()
    }
  }

  const buttonLabel = isLoading ? 'Generating...' : 'Generate Caption'

  return (
    <MultiStepMobileWizard<Values>
      schema={schema}
      onClose={onClose}
      onSubmitStep={handleSubmitStep}
      initialValues={initialValues}
      {...props}
    >
      <RefineDetails submitLabel={buttonLabel} isGenerating={isLoading} />
      {almanacFile && requestId && (
        <GeneratedContent file={almanacFile} requestId={requestId} setRequestId={setRequestId} />
      )}
    </MultiStepMobileWizard>
  )
})
